<template>
  <div class="timeframe-picker">
    <v-tooltip v-if="tooltip" top max-width="400">
      <template v-slot:activator="{ on }">
        <v-subheader v-on="on" class="pl-0 ml-0 py-0 my-0">
          <v-switch
            :input-value="value.enabled"
            :label="label"
            hide-details
            class="mt-0"
            @change="updateField('enabled', $event)"
          />
        </v-subheader>
      </template>
      <span class="caption" v-html="tooltip" />
    </v-tooltip>
    <v-subheader v-else class="pl-0 ml-0 py-0 my-0">
      <v-switch
        :input-value="value.enabled"
        :label="label"
        hide-details
        class="mt-0"
        @change="updateField('enabled', $event)"
      />
    </v-subheader>

    <v-expand-transition>
      <div v-if="value.enabled" class="timeframe-picker__body pl-1 pr-2 pb-2">
        <v-card flat class="timeframe-picker__summary pa-3 mb-3">
          <div class="caption grey--text text--lighten-1 mb-1">Selected range</div>
          <div class="subtitle-2 white--text font-weight-medium">
            {{ computed.summaryFrom }} &ndash; {{ computed.summaryTo }}
          </div>
          <div v-if="value.mode === 'relative'" class="caption mt-1 timeframe-picker__relative-caption">
            From reference {{ formatRelativeOffsetLabel(value.fromOffsetMonths, value.fromOffsetDirection) }},
            to reference {{ formatRelativeOffsetLabel(value.toOffsetMonths, value.toOffsetDirection) }}
          </div>
          <div v-if="!rangeValid" class="caption error--text mt-2">
            End month must be on or after start month.
          </div>
        </v-card>

        <v-btn-toggle
          :value="value.mode"
          mandatory
          dense
          class="timeframe-picker__mode mb-3"
          color="#2c9dd5"
          @change="updateField('mode', $event)"
        >
          <v-btn value="range" small class="text-capitalize">Fixed range</v-btn>
          <v-btn value="relative" small class="text-capitalize">Relative</v-btn>
        </v-btn-toggle>

        <div v-if="value.mode === 'range'" class="timeframe-picker__range">
          <div class="timeframe-picker__boundary mb-3">
            <div class="caption white--text mb-2">From</div>
            <v-row dense class="ma-0">
              <v-col cols="6" class="pa-0 pr-1">
                <v-select
                  :value="value.fromMonth"
                  :items="monthItems"
                  item-text="text"
                  item-value="value"
                  dense
                  outlined
                  dark
                  hide-details
                  label="Month"
                  class="timeframe-picker__field"
                  :menu-props="selectMenuProps"
                  @change="updateField('fromMonth', $event)"
                />
              </v-col>
              <v-col cols="6" class="pa-0 pl-1">
                <v-select
                  :value="value.fromYear"
                  :items="yearOptions"
                  dense
                  outlined
                  dark
                  hide-details
                  label="Year"
                  class="timeframe-picker__field"
                  :menu-props="selectMenuProps"
                  @change="updateField('fromYear', $event)"
                />
              </v-col>
            </v-row>
          </div>

          <div class="timeframe-picker__boundary">
            <div class="caption white--text mb-2">To</div>
            <v-row dense class="ma-0">
              <v-col cols="6" class="pa-0 pr-1">
                <v-select
                  :value="value.toMonth"
                  :items="monthItems"
                  item-text="text"
                  item-value="value"
                  dense
                  outlined
                  dark
                  hide-details
                  label="Month"
                  class="timeframe-picker__field"
                  :menu-props="selectMenuProps"
                  @change="updateField('toMonth', $event)"
                />
              </v-col>
              <v-col cols="6" class="pa-0 pl-1">
                <v-select
                  :value="value.toYear"
                  :items="yearOptions"
                  dense
                  outlined
                  dark
                  hide-details
                  label="Year"
                  class="timeframe-picker__field"
                  :menu-props="selectMenuProps"
                  @change="updateField('toYear', $event)"
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <div v-else class="timeframe-picker__relative">
          <div class="timeframe-picker__boundary mb-3">
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="caption white--text">Reference date</span>
              <v-btn x-small text color="#2c9dd5" class="px-1" @click="setAnchorToToday">
                Use today
              </v-btn>
            </div>
            <v-row dense class="ma-0">
              <v-col cols="6" class="pa-0 pr-1">
                <v-select
                  :value="value.anchorMonth"
                  :items="monthItems"
                  item-text="text"
                  item-value="value"
                  dense
                  outlined
                  dark
                  hide-details
                  label="Month"
                  class="timeframe-picker__field"
                  :menu-props="selectMenuProps"
                  @change="updateField('anchorMonth', $event)"
                />
              </v-col>
              <v-col cols="6" class="pa-0 pl-1">
                <v-select
                  :value="value.anchorYear"
                  :items="yearOptions"
                  dense
                  outlined
                  dark
                  hide-details
                  label="Year"
                  class="timeframe-picker__field"
                  :menu-props="selectMenuProps"
                  @change="updateField('anchorYear', $event)"
                />
              </v-col>
            </v-row>
          </div>

          <div class="timeframe-picker__boundary mb-3">
            <div class="caption white--text mb-2">From</div>
            <v-text-field
              :value="value.fromOffsetMonths"
              type="number"
              min="0"
              dense
              outlined
              dark
              hide-details
              suffix="mo"
              class="timeframe-picker__field timeframe-picker__offset-field white--text"
              @input="updateOffset('fromOffsetMonths', $event)"
            >
              <template v-slot:append>
                <v-btn-toggle
                  :value="value.fromOffsetDirection || 'after'"
                  mandatory
                  dense
                  class="timeframe-picker__inline-direction"
                  color="#2c9dd5"
                  @change="updateField('fromOffsetDirection', $event)"
                >
                  <v-btn value="before" x-small class="text-capitalize">Before</v-btn>
                  <v-btn value="after" x-small class="text-capitalize">Later</v-btn>
                </v-btn-toggle>
              </template>
            </v-text-field>
          </div>

          <div class="timeframe-picker__boundary mb-3">
            <div class="caption white--text mb-2">To</div>
            <v-text-field
              :value="value.toOffsetMonths"
              type="number"
              min="0"
              dense
              outlined
              dark
              hide-details
              suffix="mo"
              class="timeframe-picker__field timeframe-picker__offset-field white--text"
              @input="updateOffset('toOffsetMonths', $event)"
            >
              <template v-slot:append>
                <v-btn-toggle
                  :value="value.toOffsetDirection || 'after'"
                  mandatory
                  dense
                  class="timeframe-picker__inline-direction"
                  color="#2c9dd5"
                  @change="updateField('toOffsetDirection', $event)"
                >
                  <v-btn value="before" x-small class="text-capitalize">Before</v-btn>
                  <v-btn value="after" x-small class="text-capitalize">Later</v-btn>
                </v-btn-toggle>
              </template>
            </v-text-field>
          </div>

          <div class="caption white--text mb-2">Quick ranges from today</div>
          <div class="d-flex flex-wrap timeframe-picker__presets">
            <v-btn
              v-for="preset in presets"
              :key="preset.months"
              x-small
              depressed
              color="#2c9dd5"
              class="white--text mr-2 mb-2"
              @click="applyPreset(preset.months)"
            >
              Next {{ preset.label }}
            </v-btn>
          </div>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
import {
  MONTHS_SHORT,
  computeTimeframeDates,
  formatRelativeOffsetLabel,
  getTodayMonth,
  getYearOptions,
  isRangeOrderValid
} from '../utils/timeframe';

export default {
  name: 'TimeframePicker',
  props: {
    value: {
      type: Object,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    tooltip: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      selectMenuProps: {
        auto: true
      },
      yearOptions: getYearOptions(),
      presets: [
        { months: 3, label: '3 mo' },
        { months: 6, label: '6 mo' },
        { months: 12, label: '12 mo' }
      ]
    };
  },
  computed: {
    monthItems() {
      return MONTHS_SHORT.map((text, value) => ({ text, value }));
    },
    computed() {
      return computeTimeframeDates(this.value);
    },
    rangeValid() {
      return isRangeOrderValid(this.computed);
    }
  },
  methods: {
    formatRelativeOffsetLabel,
    updateField(field, fieldValue) {
      this.emitChange({ ...this.value, [field]: fieldValue });
    },
    updateOffset(field, rawValue) {
      const parsed = Math.max(0, Number(rawValue) || 0);
      this.emitChange({ ...this.value, [field]: parsed });
    },
    setAnchorToToday() {
      const today = getTodayMonth();
      this.emitChange({
        ...this.value,
        anchorYear: today.year,
        anchorMonth: today.monthIndex
      });
    },
    applyPreset(monthsAhead) {
      const today = getTodayMonth();
      this.emitChange({
        ...this.value,
        enabled: true,
        mode: 'relative',
        anchorYear: today.year,
        anchorMonth: today.monthIndex,
        fromOffsetMonths: 0,
        fromOffsetDirection: 'after',
        toOffsetMonths: monthsAhead,
        toOffsetDirection: 'after'
      });
    },
    emitChange(nextValue) {
      this.$emit('input', nextValue);
    }
  }
};
</script>

<style scoped>
.timeframe-picker__summary {
  background: rgba(255, 255, 255, 0.06) !important;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
}

.timeframe-picker__relative-caption {
  color: rgba(255, 255, 255, 0.65);
}

.timeframe-picker__mode {
  width: 100%;
}

.timeframe-picker__mode >>> .v-btn {
  flex: 1;
}

.timeframe-picker__field >>> .v-input__slot {
  min-height: 36px !important;
}

.timeframe-picker__field >>> fieldset {
  border-color: rgba(255, 255, 255, 0.28) !important;
}

.timeframe-picker__offset-field >>> .v-input__append-inner {
  margin-top: 0;
  padding-left: 4px;
  align-self: center;
}

.timeframe-picker__inline-direction {
  height: 28px;
}

.timeframe-picker__inline-direction >>> .v-btn {
  height: 28px !important;
  min-width: 52px !important;
  padding: 0 6px !important;
  font-size: 11px;
  letter-spacing: 0;
}
</style>
